"use client";

import { LocaleLink } from "@i18n/routing";
import { buildOptimizedImageUrl } from "@saas/shared/lib/image-url-builder";
import { SECTION_TITLE } from "@shared/constants/typography";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	ChevronDown,
	ChevronRight,
	Clapperboard,
	Film,
	Images,
	Layers,
	MonitorPlay,
	Move3d,
	Palette,
	ShoppingBag,
	Smile,
	Sparkles,
	Timer,
	Upload,
	Volume2,
	VolumeX,
	Wand2,
} from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
	I2V_MODES_POSTER_WIDTH,
	i2vSampleExamples,
} from "../config/sample-examples";
import type { I2VMode } from "../types";

// ---------------------------------------------------------------------------
// 工具下方的营销/SEO 正文。文案全在 `imageToVideo.<section>` 命名空间里，这里
// 只硬编码图标、区块顺序和取素材的方式。
//
// 区块顺序：模式讲解 → 硬参数 → 模型对比 → 用途 → 三步走 → prompt 技巧 →
// FAQ → CTA。模型对比表吃 grok / wan / minimax / seedance / veo 的长尾词。
// ---------------------------------------------------------------------------

// 时效信号：页面上可见的 <time>。改这里也要同步改 page.tsx 里喂 JSON-LD 的
// UPDATED_AT —— 这是 client 模块，server component 不能安全地从这里 import 常量。
const UPDATED_AT = "2026-09-25";

/** 按输入模式取一条样片当区块配图。素材换成自产的以后这里不用动。 */
const sampleFor = (mode: I2VMode) =>
	i2vSampleExamples.find((sample) => sample.mode === mode);

/**
 * 静音自动播放的样片，唯一控件是静音开关。播放跟可见性绑定，避免一进页面
 * 就把所有片子一起拉下来。poster 显式传入——本页素材路径不像 ai-video 那样
 * 能从视频地址推导出封面。
 */
function AutoPlayVideo({
	src,
	poster,
	className,
}: {
	src: string;
	poster: string;
	className?: string;
}) {
	const t = useTranslations("imageToVideo.player");
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [muted, setMuted] = useState(true);
	const { ref: inViewRef, inView } = useInView({ threshold: 0.25 });

	const setRefs = useCallback(
		(node: HTMLVideoElement | null) => {
			videoRef.current = node;
			inViewRef(node);
		},
		[inViewRef],
	);

	// React 的 muted 属性并不总能落到 DOM property 上
	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.muted = muted;
		}
	}, [muted]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}
		if (inView) {
			// 浏览器仍可能拒绝自动播放，忽略这个 rejection
			video.play().catch(() => undefined);
		} else {
			video.pause();
		}
	}, [inView]);

	return (
		<div className="relative">
			{/* biome-ignore lint/a11y/useMediaCaption: AI 生成的片子没有字幕轨 */}
			<video
				ref={setRefs}
				src={src}
				poster={poster}
				muted
				loop
				playsInline
				preload="none"
				className={className}
			/>
			<button
				type="button"
				onClick={() => setMuted((previous) => !previous)}
				aria-label={muted ? t("unmute") : t("mute")}
				className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center transition-colors"
			>
				{muted ? (
					<VolumeX className="h-4 w-4 text-white" />
				) : (
					<Volume2 className="h-4 w-4 text-white" />
				)}
			</button>
		</div>
	);
}

/* 1. 三种输入模式 —— 定义句 + 媒体/文字交替行 */
export function I2VModes() {
	const t = useTranslations("imageToVideo.modes");
	const format = useFormatter();

	const rows = (["start", "between", "reference"] as const)
		.map((mode) => ({ mode, sample: sampleFor(mode) }))
		.filter((row) => row.sample !== undefined);

	return (
		<section className="py-8 sm:py-16 bg-muted/30">
			<div className="container mx-auto px-4">
				<div className="text-center mb-10 sm:mb-16">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
					{/* 自足的定义句 —— AI 引擎和摘要卡直接整句摘走的那一句 */}
					<p className="text-base sm:text-xl text-muted-foreground/80 max-w-3xl mx-auto font-medium leading-relaxed">
						{t("definition")}
					</p>
					<p className="text-xs text-muted-foreground/70 mt-4">
						{t("poweredBy")} · {t("lastUpdatedLabel")}{" "}
						<time dateTime={UPDATED_AT}>
							{format.dateTime(new Date(UPDATED_AT), {
								year: "numeric",
								month: "long",
								timeZone: "UTC",
							})}
						</time>
					</p>
				</div>

				<div className="max-w-5xl mx-auto space-y-12 sm:space-y-20">
					{rows.map((row, i) => (
						<div
							key={row.mode}
							className={cn(
								"flex flex-col items-center gap-6 sm:gap-10",
								// 明确二选一，不依赖 twMerge 解冲突
								i % 2 === 1
									? "md:flex-row-reverse"
									: "md:flex-row",
							)}
						>
							<div className="w-full md:w-1/2">
								<AutoPlayVideo
									src={row.sample?.videoUrl ?? ""}
									poster={
										row.sample?.poster
											? buildOptimizedImageUrl(
													row.sample.poster,
													I2V_MODES_POSTER_WIDTH,
												)
											: ""
									}
									className="w-full aspect-video object-cover rounded-2xl border border-border shadow-sm bg-muted"
								/>
							</div>

							<div className="w-full md:w-1/2">
								<span className="inline-block bg-warning/30 text-foreground text-sm font-semibold px-2 py-0.5 rounded-sm mb-3">
									{t(`rows.${row.mode}.eyebrow`)}
								</span>
								<h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 leading-tight">
									{t(`rows.${row.mode}.title`)}
								</h3>
								<p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
									{t(`rows.${row.mode}.body`)}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

/* 2. 参数条 —— 只放硬数字 */
export function I2VSpecs() {
	const t = useTranslations("imageToVideo.specs");

	const specs = [
		{
			key: "models",
			icon: Sparkles,
			iconClassName: "text-primary",
			bgClassName: "bg-primary/10",
		},
		{
			key: "resolution",
			icon: MonitorPlay,
			iconClassName: "text-secondary",
			bgClassName: "bg-secondary/15",
		},
		{
			key: "duration",
			icon: Timer,
			iconClassName: "text-accent",
			bgClassName: "bg-accent/15",
		},
		{
			key: "references",
			icon: Images,
			iconClassName: "text-warning",
			bgClassName: "bg-warning/15",
		},
	];

	return (
		<section className="py-8 sm:py-14">
			<div className="container mx-auto px-4">
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
					{specs.map((spec) => {
						const Icon = spec.icon;
						return (
							<div
								key={spec.key}
								className="flex flex-col items-center text-center gap-2 rounded-xl border border-border bg-card shadow-sm p-4 sm:p-5"
							>
								<div
									className={`w-9 h-9 rounded-lg ${spec.bgClassName} flex items-center justify-center`}
								>
									<Icon
										className={`w-5 h-5 ${spec.iconClassName}`}
									/>
								</div>
								<div className="text-lg sm:text-xl font-bold text-foreground leading-tight">
									{t(`${spec.key}.value`)}
								</div>
								<div className="text-xs text-muted-foreground leading-snug">
									{t(`${spec.key}.label`)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}

type ModelRow = {
	tier: string;
	modes: string;
	resolution: string;
	duration: string;
	credits: string;
	bestFor: string;
};

/* 3. 模型对比表 —— 第一列直接是真实模型名（2026-10-06 去掉档位名和 Powered by 列），吃各家模型名的长尾 */
export function I2VModelTable() {
	const t = useTranslations("imageToVideo.models");
	const rows = t.raw("rows") as ModelRow[];

	return (
		<section className="py-8 sm:py-16">
			<div className="container mx-auto px-4">
				<div className="text-center mb-8 sm:mb-12">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
					<p className="text-base sm:text-xl text-muted-foreground/80 max-w-3xl mx-auto font-medium leading-relaxed">
						{t("subtitle")}
					</p>
				</div>

				{/* 宽表在自己的容器里横向滚动，不让 body 出现横向滚动条 */}
				<div className="max-w-5xl mx-auto overflow-x-auto rounded-2xl border border-border">
					<table className="w-full min-w-[860px] text-sm bg-card">
						<thead>
							<tr className="border-b border-border bg-muted/40">
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.tier")}
								</th>
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.modes")}
								</th>
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.resolution")}
								</th>
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.duration")}
								</th>
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.credits")}
								</th>
								<th className="text-left font-semibold p-3 sm:p-4">
									{t("headers.bestFor")}
								</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr
									key={row.tier}
									className="border-b border-border last:border-0"
								>
									<td className="p-3 sm:p-4 font-bold text-foreground whitespace-nowrap">
										{row.tier}
									</td>
									<td className="p-3 sm:p-4 text-muted-foreground">
										{row.modes}
									</td>
									<td className="p-3 sm:p-4 text-muted-foreground whitespace-nowrap">
										{row.resolution}
									</td>
									<td className="p-3 sm:p-4 text-muted-foreground whitespace-nowrap">
										{row.duration}
									</td>
									<td className="p-3 sm:p-4 text-muted-foreground">
										{row.credits}
									</td>
									<td className="p-3 sm:p-4 text-muted-foreground">
										{row.bestFor}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<p className="text-sm text-muted-foreground text-center max-w-3xl mx-auto mt-6 leading-relaxed">
					{t("footnote")}
				</p>
				{/* Grok 词由 /grok-ai-video-generator 承接，这里只导流不抢词 */}
				<p className="text-sm text-muted-foreground text-center max-w-3xl mx-auto mt-3 leading-relaxed">
					{t.rich("grokNote", {
						link: (chunks) => (
							<LocaleLink
								href="/grok-ai-video-generator"
								className="font-medium text-primary underline underline-offset-2"
							>
								{chunks}
							</LocaleLink>
						),
					})}
				</p>
			</div>
		</section>
	);
}

/* 4. 用途 —— 吃 product / portrait / social / animation 的长尾意图 */
export function I2VUseCases() {
	const t = useTranslations("imageToVideo.useCases");

	const cases = [
		{ key: "social", icon: Clapperboard },
		{ key: "product", icon: ShoppingBag },
		{ key: "portrait", icon: Smile },
		{ key: "art", icon: Palette },
	];

	return (
		<section className="py-8 sm:py-16 bg-muted/30">
			<div className="container mx-auto px-4">
				<div className="text-center mb-8 sm:mb-12">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
					{cases.map((item) => {
						const Icon = item.icon;
						return (
							<div
								key={item.key}
								className="rounded-2xl border border-border bg-card shadow-sm p-5 sm:p-6"
							>
								<div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
									<Icon className="w-6 h-6 text-primary" />
								</div>
								<h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
									{t(`items.${item.key}.title`)}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{t(`items.${item.key}.description`)}
								</p>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}

type Step = { title: string; description: string };

/* 5. 三步走 —— 同时喂 HowTo 结构化数据 */
export function I2VHowItWorks() {
	const t = useTranslations("imageToVideo.howItWorks");
	const steps = t.raw("steps") as Step[];
	const icons = [Upload, Wand2, Film];

	return (
		<section className="py-8 sm:py-16">
			<div className="container mx-auto px-4">
				<div className="text-center mb-8 sm:mb-16">
					<h2 className={`${SECTION_TITLE.className} mb-6`}>
						{t("title")}
					</h2>
					<p className="text-base sm:text-xl text-muted-foreground/80 max-w-2xl mx-auto font-medium leading-relaxed">
						{t("subtitle")}
					</p>
				</div>

				{/* 连线式流程，跟上下的卡片网格拉开对比 */}
				<div className="relative flex flex-col md:flex-row gap-10 md:gap-4 max-w-5xl mx-auto">
					<div
						aria-hidden="true"
						className="hidden md:block absolute top-7 left-[16.67%] right-[16.67%] h-px bg-border"
					/>

					{steps.map((step, i) => {
						const Icon = icons[i] ?? Film;
						return (
							<div
								key={step.title}
								className="relative flex-1 flex flex-col items-center text-center"
							>
								<div className="relative z-10 w-14 h-14 rounded-full bg-primary/10 ring-8 ring-background flex items-center justify-center mb-4">
									<Icon className="w-6 h-6 text-primary" />
									<span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
										{i + 1}
									</span>
								</div>
								<h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
									{step.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
									{step.description}
								</p>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}

type Tip = { title: string; description: string };

/* 6. Prompt 技巧 —— 吃 "how to prompt ... still images" 这类问题词 */
export function I2VPromptTips() {
	const t = useTranslations("imageToVideo.promptTips");
	const tips = t.raw("items") as Tip[];
	const icons = [Move3d, Layers, Volume2];

	return (
		<section className="py-8 sm:py-16 bg-muted/30">
			<div className="container mx-auto px-4">
				<div className="text-center mb-8 sm:mb-12">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
					{tips.map((tip, i) => {
						const Icon = icons[i] ?? Move3d;
						return (
							<div
								key={tip.title}
								className="rounded-2xl border border-border bg-card shadow-sm p-5 sm:p-6"
							>
								<div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
									<Icon className="w-6 h-6 text-primary" />
								</div>
								<h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
									{tip.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{tip.description}
								</p>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}

type FaqItem = { question: string; answer: string };

/* 7. FAQ —— 答案常驻 DOM，折叠只靠高度，收起状态下也能被抓 */
export function I2VFaq() {
	const t = useTranslations("imageToVideo.faq");
	const items = t.raw("items") as FaqItem[];
	const [openIndex, setOpenIndex] = useState<number | null>(0);

	return (
		<section className="scroll-mt-20 py-12 lg:py-20" id="faq">
			<div className="container mx-auto px-4">
				<div className="max-w-3xl mx-auto">
					<h2
						className={`${SECTION_TITLE.className} mb-10 text-center`}
					>
						{t("title")}
					</h2>

					<div className="flex flex-col">
						{items.map((item, index) => (
							<div
								key={item.question}
								className="border-b border-border"
							>
								<button
									type="button"
									onClick={() =>
										setOpenIndex(
											openIndex === index ? null : index,
										)
									}
									className="flex justify-between items-center w-full py-5 text-left gap-4"
									aria-expanded={openIndex === index}
									aria-controls={`i2v-faq-answer-${index}`}
								>
									<h3 className="text-base sm:text-lg font-semibold text-foreground">
										{item.question}
									</h3>
									<ChevronDown
										className={cn(
											"h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-200",
											openIndex === index &&
												"transform rotate-180",
										)}
									/>
								</button>
								<div
									id={`i2v-faq-answer-${index}`}
									className={cn(
										"overflow-hidden transition-all duration-300",
										openIndex === index
											? "max-h-[2000px] opacity-100 pb-5"
											: "max-h-0 opacity-0",
									)}
								>
									<p className="text-sm text-muted-foreground leading-relaxed">
										{item.answer}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

/* 8. 收尾 CTA —— /ai-edit-image、/image-to-image 也用这块，传自己的 namespace（title / description / button） */
export function I2VCtaBanner({
	namespace = "imageToVideo.cta",
}: { namespace?: string } = {}) {
	const t = useTranslations(namespace);

	return (
		<section className="py-12 sm:py-20">
			<div className="container mx-auto px-4">
				<div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-8 sm:p-14 text-center">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
					<p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
						{t("description")}
					</p>
					<Button
						size="lg"
						className="mx-auto h-12 px-8 text-sm font-medium shadow-lg shadow-blue-500/20 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
						onClick={() =>
							window.scrollTo({ top: 0, behavior: "smooth" })
						}
					>
						{t("button")}
						<ChevronRight className="h-4 w-4 ml-1" />
					</Button>
				</div>
			</div>
		</section>
	);
}
